import { CurecallBanner } from '@honestica/curecall-ui'
import { ErrorQueryHandler } from 'src/components/error/error-query-handler.component'
import { TPatientAllVariables, usePatientAllVariables, usePatientTimeline } from './patient-file.hook'
import { useLocalizedNavigate } from 'src/hooks/use-localized-navigate'
import { createContext, useContext, useEffect, useMemo } from 'react'
import { TPatient } from 'src/types/patient'
import { useSocket } from 'src/providers/socket.provider'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { localizeObject } from 'src/utils/localize-object.util'
import { useLocale } from 'src/hooks/use-locale'
import PatientFileHeader from './patient-file-header.component'
import ArchivedBanner from './archived-banner.component'
import { PatientService } from 'src/services/patient.service'
import { TPatientFollowup } from 'src/types/followup'
import { useParams } from 'react-router-dom'
import { TPatientMedAlert } from 'src/types/med-alert'
import { useTranslation } from 'react-i18next'
import { TTimeline } from '../patient-followup/_components/timeline.type'

type TCurrentPatientFile = {
  patient: TPatient
  patientFollowupsToShow: TPatientFollowup[]
  endedPatientFollowups?: TPatientFollowup[]
  currentPatientFollowup?: TPatientFollowup
  patientTimeline?: TTimeline
  patientAllVariables: TPatientAllVariables
  isPatientVariablesError: boolean
  refetchPatientVariables: () => void
}
const CurrentPatientFileContext = createContext<TCurrentPatientFile>({} as TCurrentPatientFile)
export const useCurrentPatientFile = () => useContext(CurrentPatientFileContext)

const CurrentPatientFile = ({
  children,
  projectId,
  patientId,
}: {
  children: React.ReactNode
  projectId: number
  patientId: number
}) => {
  const navigate = useLocalizedNavigate()
  const { t } = useTranslation()
  const { socket, socketError } = useSocket()
  const queryClient = useQueryClient()
  const { locale: currentLocale } = useLocale()
  const params = useParams()
  const currentPatientFollowupId = Number(params.patientFollowupId)

  const {
    isLoading: isLoadingPatient,
    error: errorPatient,
    data: patient,
  } = useQuery({
    queryKey: ['patients', projectId, patientId],
    queryFn: () => PatientService.getById(projectId, patientId),
  })
  const { error: errorTimeline, patientTimeline } = usePatientTimeline(projectId, patient)
  const {
    isLoading: isLoadingVariables,
    isError: isPatientVariablesError,
    error: errorVariables,
    refetch: refetchPatientVariables,
    patientAllVariables,
  } = usePatientAllVariables(projectId, patient)

  const patientFollowupsToShow =
    patient?.PatientFollowups?.filter(
      (patientFollowup) =>
        !patientFollowup?.Followup?.configUI?.followupUI?.patientDetails?.isHiddenOnPatientFile &&
        !patientFollowup.endReason,
    ).sort((patientFollowup1, patientFollowup2) =>
      new Date(patientFollowup1?.createdAt).getTime() > new Date(patientFollowup2?.createdAt).getTime()
        ? -1
        : 1,
    ) || []

  const endedPatientFollowups = patient?.PatientFollowups?.filter(
    (patientFollowup) => patientFollowup.endReason,
  )

  const currentPatientFollowup = patient?.PatientFollowups?.find(
    (patientFollowup) => patientFollowup.id === currentPatientFollowupId,
  )

  const finalTimeline = useMemo(() => {
    if (!patient?.PatientMedAlerts || !patientTimeline) return undefined
    const timeline = patientTimeline.timelineElements || []
    for (let i = 0; i < timeline?.length; i += 1) {
      if (timeline[i].type !== 'MedEvent') {
        continue
      }
      timeline[i].matchingPatientMedAlerts = timeline[i].PatientMedEventId
        ? patient.PatientMedAlerts.filter(
            (pma: TPatientMedAlert) => pma.PatientMedEventId === timeline[i].PatientMedEventId,
          )
        : []
    }

    return timeline.filter(({ type }: { type: string }) => ['MedEvent', 'Workflow'].includes(type))
  }, [patient?.id, patientTimeline])

  const value: TCurrentPatientFile = useMemo(
    () => ({
      patient: patient as TPatient,
      patientFollowupsToShow,
      endedPatientFollowups,
      currentPatientFollowup,
      patientTimeline: finalTimeline,
      patientAllVariables: patientAllVariables as TPatientAllVariables,
      isPatientVariablesError,
      refetchPatientVariables,
    }),
    [patient, patientFollowupsToShow?.length, finalTimeline, patientAllVariables, currentPatientFollowupId, isPatientVariablesError],
  )

  useEffect(() => {
    if (!socket || !patientId || socketError) return undefined

    const onPatientDetailsUpdated = ({ patient }: { patient: TPatient }) => {
      if (patient) {
        const patientToUpdateId = patient?.id
        const projectToUpdateId = patient?.ProjectId
        queryClient.setQueryData(
          ['patients', projectToUpdateId, patientToUpdateId],
          localizeObject(patient, currentLocale as any),
        )
      }
    }
    const onNewSms = ({ patientId: WSPatientId }: { patientId: number }) => {
      if (WSPatientId !== patientId) return
      queryClient.invalidateQueries({ queryKey: ['patients-all-variables', projectId, patientId] })
    }
    socket.emit('SUBSCRIBE_PATIENT', { patientId })
    socket.on('PATIENT_DETAILS_UPDATED', onPatientDetailsUpdated)
    socket.on('NEW_SMS', onNewSms)
    return () => {
      socket.emit('UNSUBSCRIBE_PATIENT', { patientId })
      socket.off('PATIENT_DETAILS_UPDATED', onPatientDetailsUpdated)
      socket.off('NEW_SMS', onNewSms)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, socketError, patientId, projectId])

  if (errorPatient || errorTimeline || errorVariables) {
    if (errorPatient && 'response' in errorPatient && (errorPatient as any).response?.status === 404) {
      return (
        <div className="c-mt-6">
          <CurecallBanner
            appearance="WARNING"
            actionLabel={t('pages.patient-page.patient-not-found-action')}
            action={() => navigate(`/${projectId}/medical/patients`)}
          >
            {t('pages.patient-page.patient-not-found')}
          </CurecallBanner>
        </div>
      )
    }
    return <ErrorQueryHandler error={errorPatient || errorTimeline || errorVariables} />
  }

  if (isLoadingPatient || (!patient?.isArchived && isLoadingVariables)) {
    return <div className="loading" />
  }

  return (
    <CurrentPatientFileContext.Provider value={value}>
      <div className="c-max-h-full c-flex c-flex-col !c-w-full">
        <PatientFileHeader />

        {patient?.isArchived ? <ArchivedBanner patient={patient} /> : children}
      </div>
    </CurrentPatientFileContext.Provider>
  )
}

export default CurrentPatientFile
